import { Component } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';

import {
  Weekday,
  weekDays,
  TimeRange,
  OpeningTimes,
  OpeningDay,
} from '../../../../../lead.model';
import { MatCheckbox } from '@angular/material/checkbox';
import { TranslatePipe } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatCard } from '@angular/material/card';
import { CurrentOpeningTimesService } from '../current-opening-times.service';
import { mergeOpeningTimes } from '../opening-times.utils';

export interface IntervalContainer {
  firsHH: string;
  firstMM: string;
  secondHH: string;
  secondMM: string;
}

export interface SpecificOpeningTimesInput {
  selectedDays: Weekday[];
  wholeDayOpened: boolean;
  wholeDayClosed: boolean;
  timeRanges: TimeRange[];
  update: (modify: (currentOpeningTimes: OpeningTimes) => OpeningTimes) => void;
}

@Component({
  selector: 'app-specific-opening-times',
  imports: [
    MatButton,
    MatDialogContent,
    MatDialogTitle,
    MatCheckbox,
    TranslatePipe,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDialogActions,
    MatIconButton,
    MatIcon,
    MatCard,
  ],
  templateUrl: './specific-opening-times.component.html',
  styleUrl: './specific-opening-times.component.scss',
})
export class SpecificOpeningTimesComponent {
  selectedDays: Weekday[] = [];
  checkboxWholeDayOpen = false;
  checkboxWholeDayClose = false;
  intervalContainers: IntervalContainer[];
  correctIntervalContainer = false;

  constructor(public currentOpeningTimesService: CurrentOpeningTimesService) {
    const input = currentOpeningTimesService.getSpecificOpeningTimesInput();
    this.selectedDays = input.selectedDays;
    this.checkboxWholeDayClose = input.wholeDayClosed;
    this.checkboxWholeDayOpen = input.wholeDayOpened;
    if (input.timeRanges.length === 0) {
      this.intervalContainers = [
        { firsHH: '', firstMM: '', secondHH: '', secondMM: '' },
      ];
    } else {
      this.intervalContainers = input.timeRanges.map((timeRange: TimeRange) => {
        return {
          firsHH: timeRange.start.hour.toString().padStart(2, '0'),
          firstMM: timeRange.start.minute.toString().padStart(2, '0'),
          secondHH: timeRange.end.hour.toString().padStart(2, '0'),
          secondMM: timeRange.end.minute.toString().padStart(2, '0'),
        };
      });
    }
    this.validateIntervalContainer();
  }

  removeIntervalContainer(i: number): void {
    if (this.intervalContainers.length > 1) {
      this.intervalContainers.splice(i, 1);
    } else {
      this.intervalContainers = [
        { firsHH: '', firstMM: '', secondHH: '', secondMM: '' },
      ];
      this.checkboxWholeDayClose = true;
    }
    this.validateIntervalContainer();
  }

  addIntervalContainer() {
    this.intervalContainers.push({
      firsHH: '',
      firstMM: '',
      secondHH: '',
      secondMM: '',
    });
    this.validateIntervalContainer();
  }

  getTimeRanges(): TimeRange[] {
    return this.intervalContainers.map(
      (intervalContainer: IntervalContainer) => {
        return {
          start: {
            hour: Number.parseInt(intervalContainer.firsHH),
            minute: Number.parseInt(intervalContainer.firstMM),
            second: 0,
          },
          end: {
            hour: Number.parseInt(intervalContainer.secondHH),
            minute: Number.parseInt(intervalContainer.secondMM),
            second: 0,
          },
        };
      },
    );
  }

  validateIntervalContainer(): void {
    this.correctIntervalContainer = this.intervalContainers
      .map((intervalContainer: IntervalContainer) => {
        const firstHH = Number.parseInt(intervalContainer.firsHH);
        const firstMM = Number.parseInt(intervalContainer.firstMM);
        const secondHH = Number.parseInt(intervalContainer.secondHH);
        const secondMM = Number.parseInt(intervalContainer.secondMM);
        return (
          0 <= Math.min(firstHH, secondHH, firstMM, secondMM) &&
          23 >= Math.max(firstHH, secondHH) &&
          59 >= Math.max(firstMM, secondMM)
        );
      })
      .every(Boolean);
  }

  apply() {
    if (this.checkboxWholeDayOpen) {
      this.addWholeDays();
    } else if (this.checkboxWholeDayClose) {
      this.currentOpeningTimesService.setOpeningTimes(
        this.removeWholeDays(
          this.currentOpeningTimesService.getCurrentOpeningTimes(),
        ),
      );
    } else {
      this.applyCurrentOpeningTimes();
    }
  }

  applyCurrentOpeningTimes(): void {
    const currentOpeningTimes =
      this.currentOpeningTimesService.getCurrentOpeningTimes();

    const openingTimes: OpeningTimes = { openingDays: [] };
    for (const weekDay of weekDays) {
      if (this.selectedDays.includes(weekDay)) {
        openingTimes.openingDays.push({
          weekday: weekDay,
          timeRange: this.getTimeRanges(),
        });
      }
    }

    this.currentOpeningTimesService.setOpeningTimes(
      mergeOpeningTimes(
        this.removeWholeDays(currentOpeningTimes),
        openingTimes,
      ),
    );
  }

  addWholeDays(): void {
    const currentOpeningTimes =
      this.currentOpeningTimesService.getCurrentOpeningTimes();

    const openingTimes: OpeningTimes = { openingDays: [] };
    for (const weekDay of weekDays) {
      if (this.selectedDays.includes(weekDay)) {
        openingTimes.openingDays.push({
          weekday: weekDay,
          timeRange: [
            {
              start: {
                hour: 0,
                minute: 0,
              },
              end: {
                hour: 24,
                minute: 0,
              },
            },
          ],
        });
      }
    }

    this.currentOpeningTimesService.setOpeningTimes(
      mergeOpeningTimes(
        this.removeWholeDays(currentOpeningTimes),
        openingTimes,
      ),
    );
  }
  removeWholeDays(openingTimes: OpeningTimes): OpeningTimes {
    return {
      openingDays: openingTimes.openingDays.filter(
        (openingDay: OpeningDay) =>
          !this.selectedDays.includes(openingDay.weekday),
      ),
    };
  }

  toggleDay(day: Weekday) {
    if (this.isSelected(day)) {
      this.selectedDays = this.selectedDays.filter((d) => d !== day);
    } else {
      this.selectedDays.push(day);
    }
  }

  isSelected(day: Weekday): boolean {
    return this.selectedDays.includes(day);
  }

  onInput(
    thisInput: HTMLInputElement,
    prevInput: HTMLInputElement | undefined,
    nextInput: HTMLInputElement | undefined,
  ) {
    if (thisInput.value.length === 2 && nextInput) {
      if (nextInput.value.length < 2) {
        nextInput.value = '';
      }
      nextInput.focus();
    } else if (thisInput.value.length === 0 && prevInput) {
      prevInput.focus();
    } else if (thisInput.value.length == 2 && !nextInput) {
      thisInput.blur();
    }
    this.validateIntervalContainer();
  }

  protected readonly weekDays = weekDays;
}
